import { isArray } from '@rehaje/shared'

import { UnknownSchema } from '../../constants'
import {
  SchemaMaximumError,
  SchemaMinimumError,
  SchemaTransformError
} from '../../errors'
import { normalizeNumberString } from '../../lambdas/normalizeNumberString'
import { SchemaDefinition } from '../../models/SchemaDefinition'

import type { RuntimeSchemaSpecific, SchemaBigIntSpecific } from '../../types'

export class SchemaBigIntDefinition extends SchemaDefinition {
  override defaultsMerge(
    value: any,
    schema: SchemaBigIntSpecific,
    initialize: boolean
  ) {
    if (initialize) {
      if (value === undefined) return schema.default
      return value
    }
    if (schema.required && value === undefined) {
      return schema.default
    }
    return value
  }

  override getIn(schema: SchemaBigIntSpecific, path: PropertyKey[]) {
    if (path.length === 0) return schema
    return UnknownSchema
  }

  override infer(
    target: any,
    schema: SchemaBigIntSpecific,
    path: PropertyKey[]
  ): SchemaBigIntSpecific {
    return {
      ...schema,
      type: 'Schema.BigInt',
      value: this.test(target, schema, path)
        ? (schema?.value ?? target)
        : target
    }
  }

  override inferOnlySchema(schema: SchemaBigIntSpecific) {
    return schema
  }

  override inferTypeScript(schema: SchemaBigIntSpecific): string {
    const value = schema.value
    if (value !== undefined && this.test(value, schema))
      return JSON.stringify(value)
    return 'bigint'
  }

  override prune(target: any): any {
    return target
  }

  override transform(
    target: any,
    schema: SchemaBigIntSpecific,
    path: PropertyKey[] = []
  ) {
    return this.transformToBigInt(target, schema, path)
  }

  transformToBigInt(
    value: any,
    schema: SchemaBigIntSpecific,
    path: PropertyKey[] = []
  ) {
    if (value === null && schema.nullable) return value
    const result = this.transformToBigIntNullable(value, schema, path)
    if (typeof result === 'bigint' || (result === null && schema.nullable))
      return result
    return schema.fallback === undefined ? 0n : schema.fallback
  }

  transformToBigIntNullable(
    value: any,
    schema: SchemaBigIntSpecific,
    path: PropertyKey[] = []
  ): bigint | null {
    if (isArray(value) && value.length === 1) {
      return this.transformToBigIntNullable(value[0], schema, path)
    }
    if (typeof value === 'string') {
      value = normalizeNumberString(value)
    }
    try {
      return BigInt(super.transform(value, { type: 'Schema.String' }, path))
    } catch {
      if (value !== schema.fallback)
        this.raise(new SchemaTransformError(schema.name))
      return schema.fallback ?? null
    }
  }

  override validate(target: any, schema: any): void
  override validate(
    target: any,
    schema: RuntimeSchemaSpecific<SchemaBigIntSpecific>
  ): void {
    if (this.validateMetadata(target, schema, 'bigint')) return
    if (schema.minimum && target < schema.minimum) {
      this.raise(new SchemaMinimumError(schema.name, schema.minimum))
    }
    if (schema.maximum && target > schema.maximum) {
      this.raise(new SchemaMaximumError(schema.name, schema.maximum))
    }
  }
}
